import React, { Fragment, useState } from 'react';
import { Link } from 'react-router-dom';
import PropTypes from 'prop-types';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import axios from 'axios';
import { isEmpty } from 'lodash';

import useClaimStageConfigurations from '~/components/ClaimStages/hooks/useClaimStageConfigurations';
import mixpanel from '~/components/CmsMain/mixpanel';
import Button from '~/components/core/Atomic/Buttons/Button';
import Chip from '~/components/core/Atomic/Chip/Chip';
import CircularProgress from '~/components/core/Atomic/CircularProgress/CircularProgress';
import Grid from '~/components/core/Atomic/Grid/Grid';
import InputAdornment from '~/components/core/Atomic/InputAdornment';
import Typography from '~/components/core/Atomic/Typography';
import TextField from '~/components/core/Molecules/Fields/TextField';
import OverflowArrayTextDisplayWithTooltip from '~/components/core/OverflowArrayTextWithTooltip/OverflowArrayTextDisplayWithTooltip';
import { useLobConfiguration } from '~/components/hooks/useLobConfiguration';
import PaginatedClaimSearchResults from '~/components/SearchBar/PaginatedClaimSearchResults';
import { useSysconfig } from '~/components/SystemConfiguration/SystemConfigurationScreen';
import { SEARCH_MIXPANEL_EVENTS } from '~/pocs/mixpanel';
import { CONFIGURATION_FEATURES_NAMES } from '~/Types';
import { formatSortOrder, isFeatureEnabled, reportAxiosError } from '~/Utils';
import cn from '~/Utils/cn';

import { useCms } from './hooks/useCms';
import CardDialog from './CardDialog';
import { getClaimsTableColumnsByUser } from './ClaimsTable';
import useOrganization from './OrganizationContext';
import PlainTable from './PlainTable';

const spacing = 1;

const ClaimSearch = (props) => {
  const { classes, searchValue, onChangeSearch, onSearch, disabled, errorMsg, isSearchInProgress, label, className } =
    props;

  return (
    <Card className={className}>
      <CardContent>
        {label && <Typography variant="subtitle2">{label}</Typography>}
        <form
          onSubmit={(e) => {
            e.preventDefault();
            onSearch();
          }}
        >
          <div className={classes.containerCentered}>
            <TextField
              className={cn(classes.textField, 'pr-8')}
              InputProps={{
                placeholder: 'Search for claim by: number, policy, insured or claimant name',
                endAdornment: isSearchInProgress ? (
                  <InputAdornment position="end">
                    <CircularProgress size={32} />
                  </InputAdornment>
                ) : null,
              }}
              disabled={disabled}
              fullWidth
              value={searchValue}
              onChange={(value) => onChangeSearch(value)}
              error={!!errorMsg}
              helperText={errorMsg && 'No claims found!'}
            />
            <Button
              type="submit"
              className={classes.button}
              disabled={isSearchInProgress || disabled}
              variant="contained"
              color="primary"
            >
              Search
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
};

ClaimSearch.propTypes = {
  classes: PropTypes.object.isRequired,
  searchValue: PropTypes.string.isRequired,
  onChangeSearch: PropTypes.func.isRequired,
  onSearch: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  errorMsg: PropTypes.string,
  label: PropTypes.string,
  isSearchInProgress: PropTypes.bool,
  className: PropTypes.string,
};

function DefaultSelectComponent({ claim, onSelect }) {
  return (
    <Button color="primary" component={Link} to={`/claims/${claim.id}`} onClick={() => onSelect(claim)}>
      <span className="whitespace-nowrap"> Go to claim</span>
    </Button>
  );
}

DefaultSelectComponent.propTypes = {
  claim: PropTypes.object.isRequired,
  onSelect: PropTypes.func,
};

function ClaimSearchResults({
  additionalComponent,
  preResultsTableComponent,
  searchResults,
  onCancel,
  SelectComponent,
  selectComponentProps,
  title,
  hideClaimType,
  hideLastTouchedDate,
  hideClaimStage = true,
  onClaimLinkClick,
  filteredOutColumns = [],
}) {
  const { user, userOrganization } = useCms();
  const organizationPreferences = useOrganization();
  const { lobConfigurationsDict } = useLobConfiguration();
  const isCommercialPoliciesEnabled = isFeatureEnabled(
    userOrganization,
    CONFIGURATION_FEATURES_NAMES.COMMERCIAL_POLICIES
  );
  const isConfigurableFnolEnabled = isFeatureEnabled(userOrganization, CONFIGURATION_FEATURES_NAMES.CONFIGURABLE_FNOL);
  const { isOperational: isClaimStageOperational } = useClaimStageConfigurations();

  let columnData = [
    ...getClaimsTableColumnsByUser({
      user,
      organization: userOrganization,
      trackingInfo: 'searchResults',
      onClaimLinkClick,
      organizationPreferences,
      lobConfigurationsDict,
      addClaimStageColumn: isClaimStageOperational && !hideClaimStage,
      claimStageViewOnly: true,
    }),
    {
      id: 'select_button',
      numeric: false,
      label: '',
      // eslint-disable-next-line react/display-name
      specialCell: (row) =>
        SelectComponent ? (
          <SelectComponent claim={row} onSelect={onClaimLinkClick} {...selectComponentProps} />
        ) : (
          <DefaultSelectComponent claim={row} onSelect={onClaimLinkClick} />
        ),
    },
  ];

  if (hideClaimType) {
    columnData = columnData.filter((column) => column.id !== 'type');
  }

  if (hideLastTouchedDate) {
    columnData = columnData.filter((column) => column.id !== 'last_touched');
  }

  if (isConfigurableFnolEnabled && isCommercialPoliciesEnabled) {
    const firstPartyInvolvedEntities = {
      id: 'first_party_name_chip',
      label: 'First Party Involved',
      specialCell: (row) => {
        if (isEmpty(row?.first_party_involved_entities_display_names)) {
          return null;
        }

        return (
          <div>
            <OverflowArrayTextDisplayWithTooltip
              value={row.first_party_involved_entities_display_names}
              renderItem={(label) => (
                <span key={label}>
                  <Chip label={label} size="small" className="mr-12" />
                </span>
              )}
            />
          </div>
        );
      },
      disableSort: true,
      width: '250px',
    };

    columnData.splice(1, 0, firstPartyInvolvedEntities);
  }

  if (filteredOutColumns) {
    columnData = columnData.filter((column) => !filteredOutColumns.includes(column.id));
  }

  return (
    <CardDialog
      isDialog
      title={title || 'Claims'}
      onClose={onCancel}
      maxWidth={isCommercialPoliciesEnabled ? 'lg' : false}
      fullWidth
    >
      <Grid container spacing={spacing} style={{ marginTop: '8px' }}>
        <Grid item xs={12}>
          {preResultsTableComponent}
          <PlainTable columns={columnData} rows={searchResults} />
        </Grid>
      </Grid>
      {additionalComponent}
    </CardDialog>
  );
}

ClaimSearchResults.propTypes = {
  additionalComponent: PropTypes.node,
  preResultsTableComponent: PropTypes.node,
  onCancel: PropTypes.func.isRequired,
  searchResults: PropTypes.array.isRequired,
  SelectComponent: PropTypes.func, // SelectComponent should receive claim in the props
  selectComponentProps: PropTypes.object,
  title: PropTypes.string,
  hideClaimType: PropTypes.bool,
  hideLastTouchedDate: PropTypes.bool,
  hideClaimStage: PropTypes.bool,
  onClaimLinkClick: PropTypes.func,
  filteredOutColumns: PropTypes.arrayOf(PropTypes.string),
};

const DEFAULT_SORT_BY_COLUMN = { id: 'date_of_loss', order: 'desc' };
const DEFAULT_CLAIMS_PER_PAGE = 15;

const ClaimSearchContainer = ({
  classes,
  SelectComponent,
  selectComponentProps,
  disabled,
  label,
  subOrganizationIds,
  lobs,
  limit,
  filteredOutColumns,
  coverageKeys,
  searchContainerClassName,
}) => {
  const [searchValue, setSearchValue] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_CLAIMS_PER_PAGE);
  const [sortByColumn, setSortByColumn] = useState(DEFAULT_SORT_BY_COLUMN);
  const [isSearchInProgress, setIsSearchInProgress] = useState(false);
  const [searchResults, setSearchResults] = useState(undefined);
  const [totalCount, setTotalCount] = useState(0);
  const { userOrganization } = useCms();
  const { organization } = useSysconfig();
  const fetchResults = (page, rowsPerPage, sortByColumn) => {
    setIsSearchInProgress(true);

    let searchParams = {
      search: searchValue,
      sub_organization_ids: subOrganizationIds,
      lobs,
      page: page + 1,
      rows_per_page: rowsPerPage,
      sort_by_column: formatSortOrder(sortByColumn),
    };

    if (limit) {
      searchParams = { ...searchParams, limit };
    }

    if (coverageKeys?.length > 0) {
      searchParams = { ...searchParams, coverage_keys: coverageKeys };
    }

    axios
      .get(`/api/v1/claims/organization/${organization?.id || userOrganization?.id}/search_query`, {
        params: searchParams,
      })
      .then((res) => {
        setSearchResults(res.data.claims);
        setTotalCount(res.data.count);
        setIsSearchInProgress(false);
      })
      .catch((error) => {
        setIsSearchInProgress(false);
        reportAxiosError(error);
        return null;
      });
  };

  const handleSearch = async (e) => {
    e && e.preventDefault();

    mixpanel.track(SEARCH_MIXPANEL_EVENTS.SEARCH_BUTTON_CLICKED, {
      search_value: searchValue,
    });

    const result = await fetchResults(0, rowsPerPage, sortByColumn);

    if (result) {
      mixpanel.track(SEARCH_MIXPANEL_EVENTS.SEARCH_RESULTS_RETURNED, {
        search_value: searchValue,
        num_results_returned: result.count || 0,
      });
    }
  };

  const handleChangePage = (_, newPage) => {
    setPage(newPage);
    fetchResults(newPage, rowsPerPage, sortByColumn);
  };

  const handleChangeRowsPerPage = (event) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setRowsPerPage(newRowsPerPage);
    setPage(0);
    fetchResults(0, newRowsPerPage, sortByColumn);
  };

  const handleSortByColumn = (_, newSortByColumn) => {
    setSortByColumn(newSortByColumn);
    setPage(0);
    fetchResults(0, rowsPerPage, newSortByColumn);
  };

  const handleCancelOrClaimLinkClicked = () => {
    setSearchResults(undefined);
    setPage(0);
    setIsSearchInProgress(false);
  };

  return (
    <Fragment>
      <ClaimSearch
        classes={classes}
        searchValue={searchValue}
        onChangeSearch={setSearchValue}
        onSearch={handleSearch}
        isSearchInProgress={isSearchInProgress}
        errorMsg={searchResults && searchResults.length === 0 ? 'No claims found' : ''}
        disabled={disabled}
        label={label}
        className={searchContainerClassName}
      />
      {searchResults ? (
        searchResults.length > 0 ? (
          <PaginatedClaimSearchResults
            claims={searchResults}
            count={totalCount}
            hideLastTouchedDate
            onCancel={handleCancelOrClaimLinkClicked}
            onClaimLinkClick={handleCancelOrClaimLinkClicked}
            paginationProps={{
              page,
              rowsPerPage,
              count: totalCount,
              onChangePage: handleChangePage,
              onChangeRowsPerPage: handleChangeRowsPerPage,
              rowsPerPageOptions: [5, 10, 15, 25, 50],
            }}
            onSortByColumn={handleSortByColumn}
            title="Search Results"
            preResultsTableComponent={
              totalCount > 0 && (
                <div className={classes.cardDivRow}>
                  <span className={classes.textFieldRow}>{`Found ${totalCount} matching claims`}</span>
                </div>
              )
            }
            SelectComponent={SelectComponent}
            selectComponentProps={selectComponentProps}
            filteredOutColumns={filteredOutColumns}
            className={searchContainerClassName}
          />
        ) : (
          <CardDialog isDialog noCardTitle onClose={() => setSearchResults(undefined)}>
            <Typography>No Results Were Found</Typography>
          </CardDialog>
        )
      ) : null}
    </Fragment>
  );
};

ClaimSearchContainer.propTypes = {
  classes: PropTypes.object.isRequired,
  disabled: PropTypes.bool,
  SelectComponent: PropTypes.func,
  selectComponentProps: PropTypes.object,
  label: PropTypes.string,
  subOrganizationIds: PropTypes.array,
  lobs: PropTypes.array,
  limit: PropTypes.number,
  filteredOutColumns: PropTypes.arrayOf(PropTypes.string),
  coverageKeys: PropTypes.arrayOf(PropTypes.string),
  searchContainerClassName: PropTypes.string,
};

export default ClaimSearchContainer;
export { ClaimSearchResults };
